import { FC } from 'react';
import { Tag } from '@carbon/react';
import type {
  AuditRow,
  AuditAgentRunRow,
  AuditEventRow,
  AuditSuggestionRow,
} from './types';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { formatConfidence } from 'utils/number';
import { AuditLogTable, AgentRunDetail, EventExpanded } from './audit-log';
import style from './audit-log/audit-log.module.scss';

interface Props {
  rows: AuditRow[];
  /** The feed's full size when only some of it is loaded. */
  totalCount?: number;
}

const isAgentRunRow = (row: AuditRow): row is AuditAgentRunRow =>
  row.activityType === 'agent_run';

const isEventRow = (row: AuditRow): row is AuditEventRow =>
  row.activityType === 'event';

// PEA suggestion rows always carry a non-null `addressLabel`; EMA rows
// carry `suggestedEntityId`/`suggestedEntityName`. Use the presence of
// `addressLabel` as the discriminator so we don't have to thread an
// agent-type prop through every renderer.
const isPeaSuggestion = (s: AuditSuggestionRow): boolean =>
  (s.addressLabel !== null && s.addressLabel !== undefined) ||
  (s.parsedComponents !== null && s.parsedComponents !== undefined);

const PartyMatchSuggestionRow: FC<{ suggestion: AuditSuggestionRow }> = ({
  suggestion: s,
}) => (
  <div className={style.suggestionItem}>
    <div className={style.suggestionHeader}>
      <span className={style.suggestionName}>
        {s.suggestedEntityName || `Entity #${s.suggestedEntityId ?? '—'}`}
      </span>
      <div className={style.suggestionMeta}>
        {s.processFailed ? (
          <Tag type="red" size="sm">
            Process Failed
          </Tag>
        ) : (
          <span className={style.suggestionScore}>
            {formatConfidence(s.confidenceScore)}
          </span>
        )}
        <Tag type="gray" size="sm">
          {s.status}
        </Tag>
      </div>
    </div>
    {s.reviewReason && (
      <p className={style.suggestionReason}>Review reason: {s.reviewReason}</p>
    )}
    {s.processFailed && s.error && (
      <p className={style.suggestionError}>{s.error}</p>
    )}
    {!s.processFailed &&
      s.confidenceFactors &&
      s.confidenceFactors.length > 0 && (
        <ConfidenceBreakdown factors={s.confidenceFactors} />
      )}
  </div>
);

const AddressParsingSuggestionRow: FC<{ suggestion: AuditSuggestionRow }> = ({
  suggestion: s,
}) => {
  const components = s.parsedComponents;
  const componentRows: Array<[string, string | null | undefined]> = components
    ? [
        ['Street', components.streetAddress],
        ['City', components.city],
        ['State', components.state],
        ['ZIP', components.zipCode],
      ]
    : [];
  const fmt = (v: string | null | undefined) => {
    if (v && v.trim().length > 0) return v;
    return '—';
  };

  return (
    <div className={style.suggestionItem}>
      <div className={style.suggestionHeader}>
        <span className={style.suggestionName}>Parsing Result</span>
        <div className={style.suggestionMeta}>
          <span className={style.suggestionScore}>
            {formatConfidence(s.confidenceScore)}
          </span>
          {s.acknowledgedAt ? (
            <Tag type="green" size="sm">
              Auto-accepted
            </Tag>
          ) : (
            <Tag type="warm-gray" size="sm">
              Needs review
            </Tag>
          )}
          <Tag type="gray" size="sm">
            {s.status}
          </Tag>
        </div>
      </div>
      <div className={style.parsingResult}>
        <div className={style.parsingSection}>
          <p className={style.parsingSectionLabel}>Input (as found)</p>
          <p className={style.parsingInput}>{fmt(s.addressLabel)}</p>
        </div>
        <div className={style.parsingArrow} aria-hidden>
          ↓
        </div>
        <div className={style.parsingSection}>
          <p className={style.parsingSectionLabel}>
            Output (extracted components)
          </p>
          <dl className={style.parsingComponents}>
            {componentRows.map(([label, value]) => (
              <div key={label} className={style.parsingRow}>
                <dt>{label}</dt>
                <dd>{fmt(value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
      {s.confidenceFactors && s.confidenceFactors.length > 0 && (
        <ConfidenceBreakdown factors={s.confidenceFactors} />
      )}
    </div>
  );
};

const SuggestionsList: FC<{ rows: AuditSuggestionRow[] }> = ({ rows }) => {
  if (rows.length === 0)
    return <p className={style.muted}>No suggestions persisted.</p>;
  return (
    <div className={style.suggestionsList}>
      {rows.map((s) => {
        if (isPeaSuggestion(s)) {
          return <AddressParsingSuggestionRow key={s.id} suggestion={s} />;
        }
        return <PartyMatchSuggestionRow key={s.id} suggestion={s} />;
      })}
    </div>
  );
};

const renderAgentRun = (row: AuditAgentRunRow) => {
  const d = row.data;
  // Treat any run that produced PEA-shaped suggestions as a parsing run, so the
  // section header reads "Parsing Result" instead of "Suggestions Persisted".
  // For runs with zero suggestions, fall back to the user label.
  const peaRun =
    (d.suggestions?.length ?? 0) > 0 &&
    d.suggestions!.every((s) => isPeaSuggestion(s));
  const isParsingByLabel = (d.user ?? '').includes('Provision Extraction');
  const suggestionsLabel =
    peaRun || isParsingByLabel ? 'Parsing Result' : 'Suggestions Persisted';
  return (
    <AgentRunDetail
      meta={d}
      runId={row.id}
      suggestionsLabel={suggestionsLabel}
      suggestionsCount={d.suggestions?.length ?? 0}
      suggestions={<SuggestionsList rows={d.suggestions ?? []} />}
    />
  );
};

export const PartyAuditLog: FC<Props> = ({ rows, totalCount }) => (
  <AuditLogTable
    rows={rows}
    totalCount={totalCount}
    renderExpanded={(row) => {
      return isAgentRunRow(row) ? (
        renderAgentRun(row)
      ) : isEventRow(row) ? (
        <EventExpanded
          user={row.data.user}
          action={row.data.action}
          description={row.data.description}
          details={row.data.details}
          gateTrace={row.data.gateTrace}
        />
      ) : null;
    }}
  />
);
